import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { checkAccess } from "@entrilia/shared/utilities/accessHelper.ts";
import type { ReportGroup } from "../../../../../../../api/types/svTypes.ts";
import useClientContext from "../../../../../../../context/useClientContext.ts";
import { useEnhancedBiApiClientProvider } from "../../../contexts/ApiClientProviderContext";
import { useBiClientContext } from "../../../contexts/ClientContext";
import useCurrentReportConfiguration from "../../../hooks/useCurrentReportConfiguration";
import {
  selectCurrentReport,
  selectIsReportChanged,
  selectReportEditMode,
} from "../../../store/selectors/currentReportSelectors";
import { selectReports } from "../../../store/selectors/reportsSelectors";
import { createReportsAction } from "../../../store/utils/storeUtils";
import UnsavedChangesDialog from "../../builder/backToReports/UnsavedChangesDialog";
import ReportSelectorButton from "./ReportSelectorButton";
import { replaceReportIdInUrl } from "./reportSelectorHelpers";
import ReportSelectorMenuWithGroupedList from "./ReportSelectorMenuWithGroupedList";

interface Props {
  requiredAccess: ReportAccessType;
  groups: Record<string, ReportGroup[]> | undefined;
  readonlyMode?: boolean;
}
const ReportSelector = ({ requiredAccess, groups, readonlyMode }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const { clientCode } = useClientContext();
  const { clientInfo } = useBiClientContext();
  const { getReports, updateReport } = useEnhancedBiApiClientProvider();
  const { getCurrentReportConfigurationAsync } = useCurrentReportConfiguration();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const report = useSelector(selectCurrentReport);
  const isNewReport = report?.isDraft === true;
  const isReportChanged = useSelector(selectIsReportChanged);
  const editMode = useSelector(selectReportEditMode);
  const reports = useSelector(selectReports);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [isSavingReport, setIsSavingReport] = useState(false);
  const [navigateToReportId, setNavigateToReportId] = useState<string>();
  const isMenuOpened = Boolean(anchorEl);

  const eligibleReports = useMemo(
    () => reports.filter((r) => checkAccess(r.authorization.access, requiredAccess)),
    [reports, requiredAccess]
  );

  useEffect(() => {
    return () => {
      dispatch(createReportsAction("setReports", []));
    };
  }, [dispatch]);

  const navigateToNewReport = (reportId: string) => {
    if (!report) {
      return;
    }

    navigate(replaceReportIdInUrl(window.location.pathname, report.reportId, reportId));
  };

  const handleOpenReportSelectorMenu = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);

    if (eligibleReports.length === 0) {
      setIsLoading(true);

      const [response, error] = await withErrorHandling(() => getReports({}))();

      setIsLoading(false);

      if (error) {
        logError(error, "[ReportSelector] handleOpenReportSelectorMenu");
        sendNotificationError("Failed to get reports");

        return;
      }
      dispatch(createReportsAction("setReports", response));
    }
  };

  const resetNavigateToReportId = () => {
    setNavigateToReportId(undefined);
  };

  const handleCloseReportSelectorMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectNewReport = (reportId: string) => {
    handleCloseReportSelectorMenu();
    if (editMode && isReportChanged) {
      setNavigateToReportId(reportId);
      setShowUnsavedChangesDialog(true);
    } else {
      navigateToNewReport(reportId);
    }
  };

  const handleDiscardAndContinue = () => {
    setShowUnsavedChangesDialog(false);
    if (navigateToReportId) {
      navigateToNewReport(navigateToReportId);
      resetNavigateToReportId();
    }
  };

  const handleCloseSaveDialog = () => {
    setShowUnsavedChangesDialog(false);
    resetNavigateToReportId();
  };

  const handleSaveReport = async () => {
    if (!report) {
      return;
    }

    const reportConfiguration = await getCurrentReportConfigurationAsync();

    if (!reportConfiguration) {
      return;
    }

    setIsSavingReport(true);

    const [, error] = await withErrorHandling(updateReport)({ ...report, configuration: reportConfiguration });

    setIsSavingReport(false);

    if (!error && navigateToReportId) {
      setShowUnsavedChangesDialog(false);
      navigateToNewReport(navigateToReportId);
      resetNavigateToReportId();

      return;
    }

    logError(error, "[ReportSelector] handleSaveReport");
    sendNotificationError("An error occurred while saving the report.");
  };
  const currentReportName = isNewReport ? undefined : report?.name;

  return (
    <>
      <ReportSelectorButton
        companyName={clientInfo.title}
        reportName={report?.name}
        onOpenReportSelectorMenu={handleOpenReportSelectorMenu}
        isMenuOpened={isMenuOpened}
        readonlyMode={readonlyMode ?? false}
      />
      {isMenuOpened && (
        <ReportSelectorMenuWithGroupedList
          anchorEl={anchorEl}
          clientCode={clientCode}
          currentReportName={currentReportName}
          onClose={handleCloseReportSelectorMenu}
          onSelectNewReport={handleSelectNewReport}
          groups={groups}
          reports={eligibleReports}
          isLoading={isLoading}
        />
      )}
      {showUnsavedChangesDialog && (
        <UnsavedChangesDialog
          onCancel={handleCloseSaveDialog}
          onContinue={handleDiscardAndContinue}
          onSave={handleSaveReport}
          name={report?.name}
          type="report"
          isLoading={isSavingReport}
        />
      )}
    </>
  );
};

export default ReportSelector;
